import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { CartItemAvailabilityDto } from "@lib/ShiOneClient";
import useGetCartProductAvailability from "@features/unified-user-header-feature/hooks/useGetCartProductAvailability";
import {
  AvailabilityDot,
  getAvailabilityDotStatus,
  type AvailabilityDotStatus,
} from "shared-ui/src/components/ecommerce/AvailabilityDot";
import {
  telemetryProps,
  type TelemetryPropsInput,
} from "shared-ui/src/instrumentation/gtm";

type ResolvedAvailabilityStatus = Exclude<AvailabilityDotStatus, "unverified">;

export interface ResolvedAvailability {
  status: ResolvedAvailabilityStatus;
  label?: string;
}

const CONTACT_ACCOUNT_TEAM_AVAILABILITY: ResolvedAvailability = {
  status: "unavailable",
};

interface SharedVerificationState {
  isVerifying: boolean;
  resolvedAvailability?: ResolvedAvailability;
}

interface AvailabilityVerificationContextValue {
  verificationByItemId: Record<number, SharedVerificationState | undefined>;
  beginVerification: (itemId: number) => boolean;
  finishVerification: (
    itemId: number,
    resolvedAvailability: ResolvedAvailability,
  ) => void;
  resetVerification: (itemId: number) => void;
}

const AvailabilityVerificationContext =
  createContext<AvailabilityVerificationContextValue | undefined>(undefined);

export function AvailabilityVerificationProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [verificationByItemId, setVerificationByItemId] = useState<
    Record<number, SharedVerificationState | undefined>
  >({});
  const verificationByItemIdRef = useRef(verificationByItemId);

  const updateVerificationState = useCallback((
    updater: (
      previous: Record<number, SharedVerificationState | undefined>,
    ) => Record<number, SharedVerificationState | undefined>,
  ) => {
    const next = updater(verificationByItemIdRef.current);
    verificationByItemIdRef.current = next;
    setVerificationByItemId(next);
  }, []);

  const beginVerification = useCallback((itemId: number): boolean => {
    if (verificationByItemIdRef.current[itemId]?.isVerifying) {
      return false;
    }

    updateVerificationState((previous) => ({
      ...previous,
      [itemId]: { isVerifying: true },
    }));
    return true;
  }, [updateVerificationState]);

  const finishVerification = useCallback((
    itemId: number,
    resolvedAvailability: ResolvedAvailability,
  ) => {
    updateVerificationState((previous) => ({
      ...previous,
      [itemId]: { isVerifying: false, resolvedAvailability },
    }));
  }, [updateVerificationState]);

  const resetVerification = useCallback((itemId: number) => {
    updateVerificationState((previous) => {
      const { [itemId]: _removed, ...remaining } = previous;
      return remaining;
    });
  }, [updateVerificationState]);

  const contextValue = useMemo(
    () => ({
      verificationByItemId,
      beginVerification,
      finishVerification,
      resetVerification,
    }),
    [
      beginVerification,
      finishVerification,
      resetVerification,
      verificationByItemId,
    ],
  );

  return (
    <AvailabilityVerificationContext.Provider
      value={contextValue}
    >
      {children}
    </AvailabilityVerificationContext.Provider>
  );
}

export function resolveVerifiedAvailability(
  availability: CartItemAvailabilityDto | undefined,
): ResolvedAvailability {
  if (availability?.stockDataAvailable !== true) {
    return CONTACT_ACCOUNT_TEAM_AVAILABILITY;
  }

  const itemStatus = availability.itemStatus?.trim();
  const status = getAvailabilityDotStatus(itemStatus);

  if (availability.inStock) {
    return {
      status: "in-stock",
      label:
        availability.totalUnits != null
          ? `In Stock (${availability.totalUnits} units)`
          : "In Stock",
    };
  }

  if (status === "low-stock" || status === "backorder" || status === "unavailable") {
    return { status, label: itemStatus };
  }

  return { status: "backorder", label: "Backorder" };
}

export interface AvailabilityVerifierProps {
  itemId?: number | null;
  sourceStatus?: string | null;
  telemetry: TelemetryPropsInput;
  /** Dot diameter in px, passed to AvailabilityDot (which defaults to 8). */
  size?: number;
}

export function AvailabilityVerifier({
  itemId,
  sourceStatus,
  telemetry,
  size,
}: AvailabilityVerifierProps) {
  const sharedVerification = useContext(AvailabilityVerificationContext);
  const resetSharedVerification = sharedVerification?.resetVerification;
  const hasValidItemId =
    itemId != null && Number.isSafeInteger(itemId) && itemId > 0;
  const [resolvedAvailability, setResolvedAvailability] =
    useState<ResolvedAvailability>();
  const [isVerifying, setIsVerifying] = useState(false);
  const verificationRequestId = useRef(0);
  const previousSourceKey = useRef(`${itemId ?? ""}:${sourceStatus ?? ""}`);
  const { refetch } = useGetCartProductAvailability(
    hasValidItemId ? [itemId] : [],
    false,
    { staleTime: 0 },
  );

  useEffect(() => {
    const sourceKey = `${itemId ?? ""}:${sourceStatus ?? ""}`;
    if (previousSourceKey.current === sourceKey) {
      return;
    }

    previousSourceKey.current = sourceKey;
    verificationRequestId.current += 1;
    setResolvedAvailability(undefined);
    setIsVerifying(false);
    if (hasValidItemId) {
      resetSharedVerification?.(itemId);
    }
  }, [hasValidItemId, itemId, resetSharedVerification, sourceStatus]);

  const sharedState = hasValidItemId
    ? sharedVerification?.verificationByItemId[itemId]
    : undefined;
  const displayedAvailability = sharedState?.resolvedAvailability ?? resolvedAvailability;
  const verificationInProgress = sharedState?.isVerifying ?? isVerifying;

  const verifyAvailability = async () => {
    if (verificationInProgress) {
      return;
    }

    if (!hasValidItemId) {
      setResolvedAvailability(CONTACT_ACCOUNT_TEAM_AVAILABILITY);
      return;
    }

    const requestId = verificationRequestId.current + 1;
    verificationRequestId.current = requestId;
    if (sharedVerification && !sharedVerification.beginVerification(itemId)) {
      return;
    }
    setIsVerifying(true);

    try {
      const queryResult = await refetch();
      const availability = queryResult.isError
        ? undefined
        : queryResult.data?.find((item) => item.itemId === itemId);

      if (verificationRequestId.current === requestId) {
        const resolved = resolveVerifiedAvailability(availability);
        if (sharedVerification) {
          sharedVerification.finishVerification(itemId, resolved);
        } else {
          setResolvedAvailability(resolved);
        }
      }
    } catch {
      if (verificationRequestId.current === requestId) {
        if (sharedVerification) {
          sharedVerification.finishVerification(
            itemId,
            CONTACT_ACCOUNT_TEAM_AVAILABILITY,
          );
        } else {
          setResolvedAvailability(CONTACT_ACCOUNT_TEAM_AVAILABILITY);
        }
      }
    } finally {
      if (verificationRequestId.current === requestId) {
        setIsVerifying(false);
      }
    }
  };

  if (displayedAvailability) {
    return (
      <AvailabilityDot
        status={displayedAvailability.status}
        label={displayedAvailability.label}
        size={size}
      />
    );
  }

  if (!hasValidItemId) {
    return <AvailabilityDot status="unavailable" size={size} />;
  }

  return (
    <AvailabilityDot
      status="unverified"
      size={size}
      verifying={verificationInProgress}
      onVerify={() => void verifyAvailability()}
      buttonProps={telemetryProps(telemetry)}
    />
  );
}